import { Toast } from '@cartwheel/components'
import { QueryClientProvider } from '@tanstack/react-query'
import * as React from 'react'
import { createRoot, type Root } from 'react-dom/client'
import MissingComponent from '../components/missing-component'
import { reactQueryClient } from '../lib/query-client'

// Roots are tracked here (rather than via a DOM attribute alone) so we can
// unmount them on turbo:before-cache — Turbo's cached snapshot would otherwise
// freeze the DOM, including data-react-mounted, leaving a dead React root
// behind on restore that never re-attaches to the visible DOM.
const reactRoots = new WeakMap<Element, Root>()

// Wrapper component that provides Toast context for all React components
function ToastWrapper({ children }: { children: React.ReactNode }) {
  return React.createElement(
    Toast.Provider,
    { timeout: 3000 } as React.ComponentProps<typeof Toast.Provider>,
    children,
    React.createElement(Toast.Region)
  )
}

interface CallbackDefinition {
  controller: string
  function: string
}

// vite gives us a map where the keys are file paths like "../components/test-component.tsx" and the values
// are objects containing all the named exports from the file
const modules = import.meta.glob(
  [
    '/components/**/*.{js,jsx,ts,tsx}',
    '!**/__tests__/**',
    '!**/*.test.*',
    '!**/*.spec.*',
    '!**/*.stories.*',
  ],
  { eager: true }
)

// build a map where the keys are the component names and the values are the components themselves
// biome-ignore-start lint/suspicious/noExplicitAny: we want any here
const components: { [key: string]: React.ComponentType<any> } = {}
// biome-ignore-end lint/suspicious/noExplicitAny: we want any here

for (const path in modules) {
  // get the module path without initial path delimiter or file extension
  // example: "/components/test-component.tsx" becomes "components/test-component"
  const pathName = path.replace(/^[/.]+/, '').replace(/\.[^/.]+$/, '')

  // biome-ignore-start lint/suspicious/noExplicitAny: we want any here
  const exports = modules[path] as { [key: string]: any }
  // biome-ignore-end lint/suspicious/noExplicitAny: we want any here

  // if there is a default export, it's available at the path of the file itself
  // all other exports are available at "path/exportName"
  for (const exportName in exports) {
    if (exportName === 'default') {
      components[pathName] = exports[exportName]
    } else {
      components[`${pathName}/${exportName}`] = exports[exportName]
    }
  }
}

// search the DOM for any element with a "data-react-component" attribute.
// the attribute value is used to find a matching React component, which will be rendered into that element.
// The "data-react-props" attribute can be used to pass props to the component.
// NOTE: sometimes we're inside a turbo-frame, so this listens for both turbo:load and turbo:frame-load events.
function mountReactComponents(rootElement: Document | Element) {
  const reactRootElements = rootElement.querySelectorAll(
    '[data-react-component]:not([data-react-mounted])'
  )

  for (const element of reactRootElements) {
    const componentName = element.getAttribute('data-react-component')
    const componentProps = JSON.parse(element.getAttribute('data-react-props') || '{}')

    if (!componentName) return

    const callbacksConfig = element.getAttribute('data-react-callbacks')

    if (callbacksConfig) {
      try {
        const callbacks = JSON.parse(callbacksConfig) as Record<string, CallbackDefinition>
        // biome-ignore lint/suspicious/noExplicitAny: Stimulus is attached to window at runtime
        const app = (window as any).Stimulus

        if (app) {
          for (const [propName, callbackDef] of Object.entries(callbacks)) {
            if (
              typeof callbackDef !== 'object' ||
              !callbackDef.controller ||
              !callbackDef.function
            ) {
              console.error(
                `Invalid callback format for "${propName}". Expected: { controller: "name", function: "methodName" }`
              )
              continue
            }

            const { controller: controllerName, function: methodName } = callbackDef

            const controllerElement = document.querySelector(
              `[data-controller~="${controllerName}"]`
            )
            if (!controllerElement) {
              console.error(`Controller element not found for: ${controllerName}`)
              continue
            }

            const controller = app.getControllerForElementAndIdentifier(
              controllerElement,
              controllerName
            )
            if (!controller) {
              console.error(`Controller instance not found: ${controllerName}`)
              continue
            }

            if (typeof controller[methodName] !== 'function') {
              console.error(`Method "${methodName}" not found on controller "${controllerName}"`)
              continue
            }

            componentProps[propName] = controller[methodName].bind(controller)
          }
        } else {
          console.error('Stimulus application not found on window.Stimulus')
        }
      } catch (e) {
        console.error('Error parsing react callbacks:', e)
      }
    }

    const component = components[componentName]
    const isProd = import.meta.env.MODE === 'production'

    if (!component) {
      console.error(`React component ${componentName} not found.`)
      // In production, do not render the visible fallback; just warn.
      if (isProd) {
        element.innerHTML = `Unable to render ${componentName}.`
        return
      }
    }

    const SelectedComponent = component || MissingComponent
    const props = component ? componentProps : { name: componentName }

    try {
      const root = createRoot(element)
      const componentElement = React.createElement(SelectedComponent, props)
      const wrapperElement = React.createElement(
        QueryClientProvider,
        { client: reactQueryClient },
        React.createElement(ToastWrapper, null, componentElement)
      )
      root.render(wrapperElement)
      reactRoots.set(element, root)

      // Mark as mounted to prevent double-mounting
      element.setAttribute('data-react-mounted', 'true')
    } catch (error) {
      console.error(`Error rendering React component ${componentName}`, error)
      element.innerHTML = `Failed to render ${componentName}.`
    }

    if (!component) console.warn(`React component ${componentName} not found.`)
  }
}

// Tear down React roots before Turbo caches the page snapshot.
// Without this, the cached HTML keeps data-react-mounted="true", so on a
// snapshot restore mountReactComponents skips the element and the visible DOM
// is left frozen with no live React tree behind it (Back/Forward then appear
// to do nothing because re-renders write to a detached, leaked root).
document.addEventListener('turbo:before-cache', () => {
  for (const element of document.querySelectorAll<HTMLElement>('[data-react-mounted]')) {
    const root = reactRoots.get(element)
    if (root) {
      root.unmount()
      reactRoots.delete(element)
    }
    element.removeAttribute('data-react-mounted')
  }
})

// Listen for full-page Turbo Drive navigations
document.addEventListener('turbo:load', () => {
  mountReactComponents(document)
})

// Listen for Turbo Frame loads (e.g., lazy-loaded frames, frame-scoped navigations)
document.addEventListener('turbo:frame-load', (event) => {
  mountReactComponents((event as CustomEvent).target as Element)
})

// Watch for Turbo Stream insertions that add new React component elements.
// Turbo Streams replace/append DOM nodes without firing turbo:load or turbo:frame-load,
// so a MutationObserver picks up those new nodes and mounts any React components inside them.
const observer = new MutationObserver((mutations) => {
  for (const mutation of mutations) {
    for (const node of mutation.addedNodes) {
      if (node instanceof HTMLElement) {
        if (
          node.hasAttribute('data-react-component') ||
          node.querySelector('[data-react-component]:not([data-react-mounted])')
        ) {
          const root = node.hasAttribute('data-react-component')
            ? (node.parentElement ?? document)
            : node
          mountReactComponents(root)
        }
      }
    }
  }
})

observer.observe(document.body, { childList: true, subtree: true })
